import { AnalysisCostSummarySchema, AnalysisUsageSummarySchema } from '@sweetistics/contracts/shared/analysis';
import {
  type ExternalLinkContentMetadata,
  ExternalLinkContentMetadataSchema,
} from '@sweetistics/contracts/shared/link-content';
import {
  type TIMELINE_ANALYSIS_REPORT_LENGTHS,
  type TIMELINE_ANALYSIS_TIME_RANGES,
  TimelineAnalysisMetadataPayloadSchema,
  TimelineAnalysisPromptOverridesSchema,
} from '@sweetistics/contracts/timeline-analysis';
import type {
  TimelineHighlightSchema,
  TimelineTopicSchema,
  TimelineTweetHandleSpanSchema,
  UITimelineAnalysis,
} from '@sweetistics/contracts/timeline-analysis-ui';
import { z } from 'zod';
import type { AICostSummary, AIUsageSummary } from '@/types/ai';

export type {
  TimelineTopicFeaturedLink,
  TimelineTopicFeaturedLinkComment,
} from '@sweetistics/contracts/timeline-analysis-ui';
export type ReportLength = (typeof TIMELINE_ANALYSIS_REPORT_LENGTHS)[number];

import type { PromptOverrides } from '@/domains/timeline/lib/analysis/prompt-overrides-shared';

// Time range type for timeline analysis
export type TimeRange = (typeof TIMELINE_ANALYSIS_TIME_RANGES)[number];

// Re-export ReportLength enum and type for convenience
const TimelineAnalysisMetadataBaseSchema = TimelineAnalysisMetadataPayloadSchema.unwrap();

const TimelineAnalysisMetadataCoreSchema = TimelineAnalysisMetadataBaseSchema.extend({
  id: z.string().optional().nullable(),
  providerModelId: z.string().optional(),
  mentionReactionTargetCount: z.number().optional(),
  tweetLimitOverride: z.number().optional(),
  modelHasExtendedContext: z.boolean().optional(),
  modelTotalContextTokens: z.number().optional(),
  temperature: z.number().optional(),
  viewerPreferenceSignals: z
    .object({
      likes: z.number().int().nonnegative(),
      bookmarks: z.number().int().nonnegative(),
    })
    .optional(),
  viewer: z
    .object({
      userId: z.string(),
      handle: z.string().nullable().optional(),
      displayName: z.string().nullable().optional(),
      prefersDirectAddress: z.boolean().optional(),
      isOwnerOfReport: z.boolean().optional(),
    })
    .optional(),
  customPrompt: z.string().optional(),
  filters: z
    .object({
      excludeRetweets: z.boolean().optional(),
      minEngagement: z.number().optional(),
    })
    .optional(),
  handleMap: z
    .array(
      z.object({
        handle: z.string(),
        tweetId: z.string(),
      })
    )
    .optional(),
  promptOverrides: TimelineAnalysisPromptOverridesSchema.optional(),
  promptOverridesHash: z.string().optional(),
  promptProfile: z
    .object({
      version: z.number().int(),
      sourceVersion: z.number().int().optional(),
      sections: z.array(
        z.object({
          id: z.string(),
          heading: z.string(),
          instructions: z.string(),
          source: z.enum(['default', 'profile', 'custom']).optional(),
          enabled: z.boolean().optional(),
          overrideInstructions: z.string().optional(),
        })
      ),
    })
    .optional(),
  collectionBreakdown: z
    .object({
      totalBudget: z.number(),
      timeline: z.object({ fetched: z.number(), used: z.number() }),
      mentions: z.object({ fetched: z.number(), used: z.number() }),
      likes: z.object({ requested: z.number(), used: z.number() }),
      bookmarks: z.object({ requested: z.number(), used: z.number() }),
      referencesAdded: z.number(),
    })
    .optional(),
  usage: AnalysisUsageSummarySchema.optional(),
  cost: AnalysisCostSummarySchema.optional(),
  estimatedCharacterCount: z.number().optional(),
  linkContent: ExternalLinkContentMetadataSchema.optional(),
}).catchall(z.unknown());

export const TimelineAnalysisMetadataSchema = TimelineAnalysisMetadataCoreSchema;

export const NullableTimelineAnalysisMetadataSchema = TimelineAnalysisMetadataSchema.nullable();

type PartialUsageSummary = Partial<AIUsageSummary>;

type PartialCostSummary = Partial<AICostSummary>;

type TimelineAnalysisMetadataSchemaType = z.infer<typeof TimelineAnalysisMetadataSchema>;

export type TimelineAnalysisMetadata = Omit<TimelineAnalysisMetadataSchemaType, 'model' | 'requestedModel'> & {
  model?: string | undefined;
  requestedModel?: string | undefined;
  createdAt?: Date | undefined;
  id?: string | null | undefined;
  usage?: PartialUsageSummary | undefined;
  cost?: PartialCostSummary | undefined;
  collectionBreakdown?:
    | {
        totalBudget: number;
        timeline: { fetched: number; used: number };
        mentions: { fetched: number; used: number };
        likes: { requested: number; used: number };
        bookmarks: { requested: number; used: number };
        referencesAdded: number;
      }
    | undefined;
  handleMap?: Array<{ handle: string; tweetId: string }> | undefined;
  promptOverrides?:
    | {
        sections?: Array<{ id: string; enabled: boolean; instructions?: string | undefined }> | undefined;
        customInstructions?: string | undefined;
        markdownAppendix?: string | undefined;
        jsonNotes?: string | undefined;
        totalLength?: number | undefined;
      }
    | undefined;
  promptOverridesHash?: string | undefined;
  promptProfile?:
    | {
        version: number;
        sourceVersion?: number | undefined;
        sections: Array<{
          id: string;
          heading: string;
          instructions: string;
          source?: 'default' | 'profile' | 'custom' | undefined;
          enabled?: boolean | undefined;
          overrideInstructions?: string | undefined;
        }>;
      }
    | undefined;
  viewer?:
    | {
        userId: string;
        handle?: string | null | undefined;
        displayName?: string | null | undefined;
        prefersDirectAddress?: boolean | undefined;
        isOwnerOfReport?: boolean | undefined;
      }
    | undefined;
  linkContent?: ExternalLinkContentMetadata | null | undefined;
};

export type NullableTimelineAnalysisMetadata = z.infer<typeof NullableTimelineAnalysisMetadataSchema>;

export interface DailyDigestSettings {
  enabled: boolean;
  report_length: ReportLength;
  schedule_hour: number; // 0-23
  time_range: '12h' | '24h' | '48h';
  timezone: string;
}

export interface NotificationSettings {
  email_enabled: boolean;
  push_subscription?: PushSubscription; // Web Push subscription
  send_on_complete: boolean;
  web_push_enabled: boolean;
}

export type TweetHandleSpan = z.infer<typeof TimelineTweetHandleSpanSchema>;

export type TimelineAnalysisTopic = z.infer<typeof TimelineTopicSchema>;

export type TimelineAnalysisHighlight = z.infer<typeof TimelineHighlightSchema>;

export interface TimelineTweetsAnalyzedMetadata {
  count: number;
  dateRange?:
    | {
        min: string | Date;
        max: string | Date;
      }
    | null
    | undefined;
  firstTweetId?: string | null | undefined;
  lastTweetId?: string | null | undefined;
  sampleTweetIds?: string[] | undefined;
}

export interface TimelineAiMetadata {
  generationTimeMs?: number | undefined;
  model?: string | undefined;
  timestamp?: string | undefined;
  tweetsAnalyzed?: TimelineTweetsAnalyzedMetadata | undefined;
}

export type TimelineAnalysis = UITimelineAnalysis & {
  aiMetadata?: TimelineAiMetadata | undefined;
};

// Request/Response types
export interface CreateAnalysisRequest {
  forceNew?: boolean;
  // Optional custom window for first-class "since last" analyses
  fromTimestamp?: string; // ISO string
  immediate?: boolean;
  maxTweets?: number;
  model?: string;
  preferenceSignals?: boolean;
  promptOverrides?: PromptOverrides | null;
  reportLength?: ReportLength;
  timeRange: string;
  toTimestamp?: string; // ISO string
}

export interface CreateAnalysisResponse {
  analysis?: TimelineAnalysis; // Include if cached
  analysisId: string;
  cached: boolean;
  message?: string;
  metadata?: TimelineAnalysisMetadata;
  success: boolean;
  token?: TimelineRealtimeToken;
}

export interface TimelineRealtimeToken {
  channel: string | { name: string };
  key?: string;
  topics: string[];
}

export interface TimelineAnalysisCache {
  analysis: TimelineAnalysis;
  createdAt: Date;
  expiresAt: Date | null;
  id: string;
  lastAccessedAt: Date;
  metadata: TimelineAnalysisMetadata;
  timeRange: string;
  userId: string;
}

export interface AnalysisHistoryResponse {
  newTweetCounts: Record<string, number>;
  reports: TimelineAnalysisCache[];
}

// ReportLength already re-exported at top of file
